import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { withConflictConfirm } from '../availability/conflicts';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { EventPicker } from '../events/EventPicker';
import { eventName, type DatedEvent } from '../events/calendar';
import { formatEventRange } from '../events/labels';
import { trapTab } from '../ui/focus';
import { PACKLISTS_RESOURCE, packlistKey } from './types';
import { DialogLayer } from '../ui/Dialog';

export type CopyGroup = 'manual' | 'kits' | 'blocks';
export const GROUP_LABELS: Record<CopyGroup, string> = { manual: 'Pozycje pojedyncze', kits: 'Zestawy', blocks: 'Bloki i szablony' };
const GROUP_HINTS: Record<CopyGroup, string> = { manual: 'dodane ręcznie i z kosztorysu', kits: 'rozwinięte na sprzęt', blocks: 'wstawione bloki i szablony' };

export type CopyPreview = {
  source: { id: string; name?: string; startsAt?: string; endsAt?: string };
  lines: Array<{ itemId: string; itemName: string; parts: Record<CopyGroup, number>; ownDays: boolean; firstDay?: string; lastDay?: string; notes?: string; inTarget: number; free?: number }>;
  kits: Array<{ name: string; quantity: number; group: CopyGroup }>;
  targetLoaded: boolean;
  targetLines: number;
};

/** What a line brings with the chosen groups. */
export const chosenQuantity = (parts: Record<CopyGroup, number>, groups: ReadonlySet<CopyGroup>): number => (Object.keys(parts) as CopyGroup[]).reduce((n, g) => n + (groups.has(g) ? parts[g] : 0), 0);

/**
 * What would be missing after the copy (preview, the server tells the truth afterwards): what the line brings, plus —
 * when added to the current packlist — what the target's line of the same item and days has already (the line it joins),
 * beyond what is free in those days.
 */
export const missingAfter = (line: CopyPreview['lines'][number], quantity: number, mode: 'add' | 'replace'): number =>
  line.free === undefined ? 0 : Math.max(0, quantity + (mode === 'add' ? line.inTarget : 0) - line.free);

const shortDay = (day: string) => day.split('-').reverse().slice(0, 2).join('.');

/**
 * "Kopiuj z wydarzenia" (owner 2026-10-21 06:58, the packlist's "…"): pick the event (calendar first, or the list),
 * see what comes, choose "Dodaj do obecnej" / "Zastąp obecną", the groups and whether lines keep their own days.
 */
export function CopyFromEventDialog({ eventId, onClose, onCopied }: { eventId: string; onClose: () => void; onCopied: (notice: string) => void }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const [source, setSource] = useState<DatedEvent | null>(null);
  const [mode, setMode] = useState<'add' | 'replace'>('add');
  const [groups, setGroups] = useState<ReadonlySet<CopyGroup>>(new Set(['manual', 'kits', 'blocks']));
  const [keepOwnDays, setKeepOwnDays] = useState(false);
  // the preview checks the days the copy will use (own days kept or the event's): asked again when that changes
  const preview = useQuery({
    queryKey: ['packlists', eventId, 'copy-from', source?.id, keepOwnDays],
    enabled: !!source,
    placeholderData: (previous) => previous,
    queryFn: () => api<CopyPreview>(`/api/packlists/${eventId}/copy-from/${source!.id}${keepOwnDays ? '?keepOwnDays=true' : ''}`),
  });
  const copy = useMutation({
    // gear other events reserved first: one „Dodaj mimo to” / „Anuluj” for the whole copy (owner 2026-10-24)
    mutationFn: () =>
      withConflictConfirm((confirmConflicts) =>
        api<{ copied: number; shortages: number }>(`/api/packlists/${eventId}/copy-from`, { method: 'POST', body: { sourceEventId: source!.id, mode, groups: [...groups], keepOwnDays, ...(confirmConflicts ? { confirmConflicts } : {}) } }),
      ),
    onSuccess: async (r) => {
      if (!r) return;
      await queryClient.invalidateQueries({ queryKey: packlistKey(eventId) });
      const name = source ? eventName(source) : '';
      onCopied(
        `Skopiowano ${r.copied} ${r.copied === 1 ? 'pozycję' : 'poz.'} z „${name}”.` +
          (r.shortages > 0 ? ` Brakuje sprzętu w ${r.shortages} ${r.shortages === 1 ? 'pozycji' : 'pozycjach'} — są na czerwono.` : ''),
      );
    },
  });
  if (!me) return null;
  const p = preview.data;
  const counts: Record<CopyGroup, number> = { manual: 0, kits: 0, blocks: 0 };
  for (const l of p?.lines ?? []) for (const g of Object.keys(counts) as CopyGroup[]) if (l.parts[g] > 0) counts[g]++;
  const present = (Object.keys(counts) as CopyGroup[]).filter((g) => counts[g] > 0);
  const rows = (p?.lines ?? []).map((l) => ({ line: l, quantity: chosenQuantity(l.parts, groups) })).filter((r) => r.quantity > 0);
  const ownDays = (p?.lines ?? []).filter((l) => l.ownDays).length;
  const canKeepDays = ownDays > 0 && fieldVisible(me, PACKLISTS_RESOURCE, 'firstDay');
  const short = rows.filter((r) => missingAfter(r.line, r.quantity, mode) > 0);
  const error = (copy.error ?? preview.error) instanceof ApiError ? ((copy.error ?? preview.error) as ApiError) : null;
  const toggle = (g: CopyGroup) => {
    const next = new Set(groups);
    if (next.has(g)) next.delete(g);
    else next.add(g);
    setGroups(next);
  };

  return (
    <div className="dialog-backdrop">
      <div
        className="dialog panel copy-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="copy-from-title"
        data-feature-id="packlists.copy_from"
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
          <DialogLayer onClose={onClose} busy={copy.isPending} />
        <h2 id="copy-from-title">Kopiuj z wydarzenia</h2>
        {!source && (
          <>
            <p className="muted">Kliknij wydarzenie w kalendarzu albo wyszukaj je na liście.</p>
            <EventPicker excludeId={eventId} onPick={setSource} label="Skąd kopiować" />
          </>
        )}
        {source && (
          <>
            <p className="copy-source">
              <span>
                Z wydarzenia <strong>{eventName(source)}</strong> <span className="muted">· {formatEventRange(source.startsAt, source.endsAt)}</span>
              </span>
              <button className="btn ghost small" type="button" onClick={() => { setSource(null); copy.reset(); }}>
                Zmień
              </button>
            </p>
            {preview.isLoading && <p className="muted" role="status">Wczytywanie sprzętówki…</p>}
            {p && p.lines.length === 0 && <p className="muted">Sprzętówka tego wydarzenia jest pusta.</p>}
            {p && p.lines.length > 0 && (
              <>
                <fieldset className="segmented copy-mode">
                  <legend>Do tej sprzętówki</legend>
                  <label className={mode === 'add' ? 'on' : undefined}>
                    <input type="radio" name="copy-mode" checked={mode === 'add'} onChange={() => setMode('add')} />
                    Dodaj do obecnej
                  </label>
                  <label className={mode === 'replace' ? 'on' : undefined}>
                    <input type="radio" name="copy-mode" checked={mode === 'replace'} disabled={p.targetLoaded} onChange={() => setMode('replace')} />
                    Zastąp obecną
                  </label>
                </fieldset>
                <p className="hint">
                  {mode === 'add'
                    ? p.targetLines > 0
                      ? 'Ten sam sprzęt w tych samych dniach doliczy się do istniejącej pozycji.'
                      : 'Sprzętówka jest pusta — pozycje trafią do niej w całości.'
                    : 'Znikną pozycje dodane ręcznie, z zestawów, bloków i szablonów. Zostaje to, co dodał kosztorys tego wydarzenia; reguły policzą się od nowa.'}
                  {p.targetLoaded && ' Zastąpić nie można: część sprzętu jest już załadowana.'}
                </p>
                <fieldset className="copy-groups">
                  <legend>Co skopiować</legend>
                  {present.map((g) => (
                    <label key={g} className="check">
                      <input type="checkbox" checked={groups.has(g)} onChange={() => toggle(g)} />
                      <span>
                        {GROUP_LABELS[g]} <span className="muted">({counts[g]}, {GROUP_HINTS[g]})</span>
                      </span>
                    </label>
                  ))}
                  {canKeepDays && (
                    <label className="check">
                      <input type="checkbox" checked={keepOwnDays} onChange={(e) => setKeepOwnDays(e.target.checked)} />
                      <span>
                        Zachowaj własne dni pozycji <span className="muted">({ownDays}; bez tego wszystko na dni tego wydarzenia)</span>
                      </span>
                    </label>
                  )}
                </fieldset>
                <div className="copy-preview">
                  <table className="table compact" aria-label="Co zostanie skopiowane">
                    <thead>
                      <tr>
                        <th scope="col">Sprzęt</th>
                        <th scope="col" className="num">
                          Ilość
                        </th>
                        <th scope="col">Dni</th>
                        {rows.some((r) => r.line.free !== undefined) && <th scope="col">W terminie</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(({ line, quantity }, i) => {
                        const missing = missingAfter(line, quantity, mode);
                        return (
                          <tr key={`${line.itemId}-${i}`} className={missing > 0 ? 'short' : undefined}>
                            <td>
                              {line.itemName}
                              {line.notes ? <span className="muted"> — {line.notes}</span> : null}
                            </td>
                            <td className="num">{quantity}</td>
                            <td className="muted">{line.ownDays && keepOwnDays && line.firstDay && line.lastDay ? (line.firstDay === line.lastDay ? shortDay(line.firstDay) : `${shortDay(line.firstDay)}–${shortDay(line.lastDay)}`) : 'jak wydarzenie'}</td>
                            {line.free !== undefined && <td>{missing > 0 ? <span className="short-text">brakuje {missing}</span> : <span className="muted">wolne</span>}</td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className={short.length > 0 ? 'alert' : 'muted'} role={short.length > 0 ? 'alert' : undefined}>
                  {rows.length === 0
                    ? 'Zaznacz, co skopiować.'
                    : `Skopiujesz ${rows.length} ${rows.length === 1 ? 'pozycję' : 'poz.'} (${rows.reduce((n, r) => n + r.quantity, 0)} szt.).` +
                      (short.length > 0 ? ` W terminie tego wydarzenia brakuje sprzętu: ${short.length} ${short.length === 1 ? 'pozycja' : 'poz.'} — skopiujesz je mimo to, rezerwacje pokażą braki.` : '') +
                      ' Reguły i przypomnienia tego wydarzenia zadziałają same.'}
                </p>
              </>
            )}
          </>
        )}
        {error && (
          <p className="alert" role="alert">
            {error.message}
          </p>
        )}
        <div className="actions">
          {source && p && p.lines.length > 0 && (
            <button className="btn primary" type="button" disabled={copy.isPending || rows.length === 0} onClick={() => copy.mutate()}>
              {copy.isPending ? 'Kopiowanie…' : mode === 'replace' ? 'Zastąp sprzętówkę' : 'Kopiuj do sprzętówki'}
            </button>
          )}
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
